// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Legend } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { TableHeader } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import TableLayout from '../../../components/TableLayoutV2';
import { PADDING as TABLE_LAYOUT_PADDING } from '../../../components/TableLayoutV2/constants';
import useIntl from '../../../hooks/useIntl';
import ManageGrossRentsEmptyState from '../ManageGrossRentsEmptyState';
import GrossRentCertificationsTable from './GrossRentCertificationsTable/index.jsx';
import CompleteCertificationsModal from './CompleteCertificationsModal/index.jsx';
import useGrossRentCertifications, {
  useEffectiveDateFilter,
  useManageGrossRentsMenu,
} from './hooks';
import messages from './messages';

const TABLE_LAYOUT_NAME = 'gross-rent-certifications-tab';

const GrossRentCertificationsTab = () => {
  const intl = useIntl();

  const { grossRentCertifications, isLoading, isError, isRetrying, refetch } =
    useGrossRentCertifications();

  const {
    filteredCertifications,
    selectOptions,
    selectedEffectiveDate,
    setSelectedEffectiveDate,
  } = useEffectiveDateFilter(grossRentCertifications);

  const {
    completeModalProps,
    handleMenuItemClick,
    isMenuDisabled,
    menuDisabledTooltip,
    isSubmittingComplete,
    isSubmittingGenerateAll,
    menuOptions,
  } = useManageGrossRentsMenu({
    allCertifications: grossRentCertifications,
    certifications: filteredCertifications,
    intl,
    refetch,
    selectedEffectiveDate,
  });

  if (isLoading) {
    return (
      <AsyncBox
        loading
        sx={{
          mt: 3,
          minHeight: 400,
        }}
      />
    );
  }

  if (isError) {
    return (
      <ManageGrossRentsEmptyState
        message={messages.loadError}
        retryMessage={messages.retry}
        onRetry={refetch}
        isRetrying={isRetrying}
        sx={{ mt: 3 }}
      />
    );
  }

  return (
    <TableLayout
      name={TABLE_LAYOUT_NAME}
      containerElementsHeight={16}
      childrenElementsHeight={-TABLE_LAYOUT_PADDING}
      sx={{ mt: 3, p: 0 }}
    >
      {(tableHeight) => (
        <>
          <Stack spacing={2}>
            <TableHeader
              title=""
              count={filteredCertifications.length}
              countName={intl.formatMessage(messages.certificationsCountName)}
            />

            <Legend
              sx={{ paddingX: 0 }}
              data={[
                {
                  key: 'missingData',
                  label: (
                    <b>
                      <FormattedMessage {...messages.missingDataLegend} />
                    </b>
                  ),
                  variant: 'warning',
                },
              ]}
            />

            <Box
              component="ul"
              id="grossRentCertificationNotes"
              sx={{ m: 0, pl: 2.25 }}
            >
              <Typography
                variant="bodySmall"
                color="text.secondary"
                component="li"
              >
                <FormattedMessage
                  {...messages.missingPriorCertBullet}
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                component="li"
              >
                <FormattedMessage
                  {...messages.noChangeBullet}
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                component="li"
              >
                <FormattedMessage {...messages.grossRentHistoryBullet} />
              </Typography>
            </Box>
          </Stack>

          <GrossRentCertificationsTable
            grossRentCertifications={filteredCertifications}
            handleMenuItemClick={handleMenuItemClick}
            isMenuDisabled={isMenuDisabled}
            menuDisabledTooltip={menuDisabledTooltip}
            isSubmittingComplete={isSubmittingComplete}
            isSubmittingGenerateAll={isSubmittingGenerateAll}
            menuOptions={menuOptions}
            onEffectiveDateChange={setSelectedEffectiveDate}
            refetch={refetch}
            selectOptions={selectOptions}
            selectedEffectiveDate={selectedEffectiveDate}
            tableHeight={tableHeight}
          />
          <CompleteCertificationsModal {...completeModalProps} />
        </>
      )}
    </TableLayout>
  );
};

export default GrossRentCertificationsTab;
